// ExternalIssueImportDialog — upload ESLint / RuboCop / SARIF / generic-JSON
// reports and import them as orbital issues via POST /external-issues (T272/T255).

import { FormEvent, useEffect, useRef, useState } from 'react';
import { FileUp, Loader2, Upload, X } from 'lucide-react';
import {
  ApiError,
  type ExternalIssueFormat,
  externalIssuesApi,
} from '../lib/api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Props {
  readonly projectID: string;
  readonly onClose: () => void;
  readonly onImported: () => void;
}

interface FormatOption {
  value: ExternalIssueFormat;
  label: string;
  hint: string;
}

const FORMAT_OPTIONS: FormatOption[] = [
  { value: 'eslint', label: 'ESLint JSON', hint: 'eslint --format json output' },
  { value: 'rubocop', label: 'RuboCop JSON', hint: 'rubocop --format json output' },
  { value: 'sarif', label: 'SARIF 2.1', hint: 'Static Analysis Results Interchange Format' },
  { value: 'generic', label: 'Generic JSON', hint: '[{rule_id, message, file_path, line, …}]' },
];

// ---------------------------------------------------------------------------
// Dialog
// ---------------------------------------------------------------------------

export function ExternalIssueImportDialog({ projectID, onClose, onImported }: Props) {
  const [format, setFormat] = useState<ExternalIssueFormat>('eslint');
  const [branch, setBranch] = useState('main');
  const [json, setJson] = useState('');
  const [fileName, setFileName] = useState('');
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<{ ok: boolean; msg: string } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (ev) => {
      setJson((ev.target?.result as string) ?? '');
    };
    reader.readAsText(file);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!json.trim()) {
      setStatus({ ok: false, msg: 'Paste or upload a report first.' });
      return;
    }

    let parsed: unknown;
    try {
      parsed = JSON.parse(json);
    } catch {
      setStatus({ ok: false, msg: 'Report is not valid JSON.' });
      return;
    }

    setBusy(true);
    setStatus(null);
    try {
      const res = await externalIssuesApi.import(projectID, format, parsed, branch || 'main');
      setStatus({
        ok: true,
        msg: `Imported ${res.imported} issue${res.imported === 1 ? '' : 's'}. Scan ID: ${res.scan_id.slice(0, 8)}…`,
      });
      setJson('');
      setFileName('');
      if (fileRef.current) fileRef.current.value = '';
      onImported();
    } catch (err) {
      let msg: string;
      if (err instanceof ApiError) {
        msg = `${err.code}: ${err.message}`;
      } else if (err instanceof Error) {
        msg = err.message;
      } else {
        msg = 'Import failed.';
      }
      setStatus({ ok: false, msg });
    } finally {
      setBusy(false);
    }
  };

  return (
    <dialog
      ref={dialogRef}
      onClose={onClose}
      aria-label="Import external issues"
      className="p-0 m-0 bg-transparent border-0 max-w-none w-screen h-screen backdrop:bg-black/50"
    >
      <div className="flex items-center justify-center w-full h-full">
        {/* Backdrop button for click-outside-to-close */}
        <button
          type="button"
          onClick={onClose}
          aria-hidden="true"
          tabIndex={-1}
          className="absolute inset-0 h-full w-full cursor-default bg-transparent border-none"
        />

        {/* Panel */}
        <div className="relative z-10 w-full max-w-lg mx-4 orb-card p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display text-lg font-semibold text-fg flex items-center gap-2">
            <FileUp className="h-5 w-5 text-accent" aria-hidden="true" />
            Import External Issues
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close dialog"
            className="text-fg-muted hover:text-fg transition-colors"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Format */}
          <div>
            <label htmlFor="ext-format" className="orb-section-title block mb-1.5">
              Format
            </label>
            <select
              id="ext-format"
              value={format}
              onChange={(e) => setFormat(e.target.value as ExternalIssueFormat)}
              className="
                w-full rounded-md border border-border bg-bg-inset px-3 py-2
                text-sm text-fg focus:outline-none focus:ring-2 focus:ring-accent/40
              "
            >
              {FORMAT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label} — {o.hint}
                </option>
              ))}
            </select>
          </div>

          {/* Branch */}
          <div>
            <label htmlFor="ext-branch" className="orb-section-title block mb-1.5">
              Branch
            </label>
            <input
              id="ext-branch"
              type="text"
              value={branch}
              onChange={(e) => setBranch(e.target.value)}
              placeholder="main"
              className="
                w-full rounded-md border border-border bg-bg-inset px-3 py-2
                text-sm text-fg placeholder:text-fg-subtle
                focus:outline-none focus:ring-2 focus:ring-accent/40
              "
            />
            <p className="text-xs text-fg-muted mt-1">
              Issues are attached to this branch. Defaults to "main" if left blank.
            </p>
          </div>

          {/* File upload */}
          <div>
            <p className="orb-section-title mb-1.5">Report</p>
            <div className="flex items-center gap-3 mb-2">
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="
                  inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs
                  font-semibold border border-border text-fg-muted hover:text-fg
                  hover:border-fg-muted transition-colors
                "
              >
                <Upload className="h-3.5 w-3.5" aria-hidden="true" />
                Upload file
              </button>
              {fileName && (
                <span className="text-xs text-fg-muted truncate max-w-[12rem]">{fileName}</span>
              )}
              <input
                ref={fileRef}
                type="file"
                accept=".json,.sarif"
                onChange={handleFile}
                className="hidden"
                aria-label="Upload report file"
              />
            </div>
            <textarea
              value={json}
              onChange={(e) => setJson(e.target.value)}
              placeholder="…or paste the report JSON here"
              rows={7}
              spellCheck={false}
              className="
                w-full rounded-md border border-border bg-bg-inset px-3 py-2
                text-xs font-mono text-fg placeholder:text-fg-subtle resize-y
                focus:outline-none focus:ring-2 focus:ring-accent/40
              "
            />
          </div>

          {/* Status */}
          {status && (
            status.ok ? (
              <output
                className="text-xs rounded-md px-3 py-2 border text-fg-muted bg-accent/5 border-accent/20 block"
              >
                {status.msg}
              </output>
            ) : (
              <p
                role="alert"
                className="text-xs rounded-md px-3 py-2 border text-danger bg-danger/5 border-danger/20"
              >
                {status.msg}
              </p>
            )
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="
                px-4 py-2 rounded-md text-sm font-semibold border border-border
                text-fg-muted hover:text-fg hover:border-fg-muted transition-colors
              "
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy}
              className="
                inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm
                font-semibold bg-accent text-white hover:bg-accent/90
                disabled:opacity-50 disabled:cursor-not-allowed transition-colors
              "
            >
              {busy ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                  Importing…
                </>
              ) : (
                <>
                  <FileUp className="h-3.5 w-3.5" aria-hidden="true" />
                  Import
                </>
              )}
            </button>
          </div>
        </form>
        </div>
      </div>
    </dialog>
  );
}
